import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Quote, Sparkles, Heart, Wrench, Truck } from "lucide-react";
import { useLang } from "@/lib/i18n";
import { useSEO, breadcrumbJsonLd, siteUrl } from "@/lib/seo";
import CTASection from "@/components/CTASection";

// Photos uploaded later — keep paths stable so customer can drop them in
const STORY_PHOTOS = {
  bus: "/images/story-eerste-busje.jpg",
  pomp: "/images/story-losse-pomp.jpg",
  begin: "/images/story-beginjaren.jpg",
  werk: "/images/story-werkdag.jpg",
};

const Story = () => {
  const { lang } = useLang();

  useSEO({
    title: lang === "nl"
      ? "Het Verhaal Achter Wasz.nl — Bijna 10 jaar mobiele truckwash met passie"
      : "The Story Behind Wasz.nl — Nearly 10 years of mobile truckwash with passion",
    description: lang === "nl"
      ? "Van een losse pomp en een goedkoop busje naar een mobiele truckwash voor heel transport-Nederland. Het echte verhaal achter Wasz.nl: oprichting, eerste klanten, en waarom kwaliteit altijd boven snelheid staat."
      : "From a single pump and a cheap van to mobile truckwash across the Netherlands. The real story behind Wasz.nl: how it started, the first customers and why quality always beats speed.",
    canonical: siteUrl("/het-verhaal-achter-wasz"),
    image: siteUrl("/og-image-ai.jpg?v=4"),
    lang,
    jsonLd: [
      {
        id: "story-breadcrumb",
        data: breadcrumbJsonLd([
          { name: "Home", url: siteUrl("/") },
          { name: lang === "nl" ? "Het Verhaal" : "Our Story", url: siteUrl("/het-verhaal-achter-wasz") },
        ]),
      },
      {
        id: "story-article",
        data: {
          "@context": "https://schema.org",
          "@type": "AboutPage",
          mainEntity: {
            "@type": "Organization",
            name: "Wasz.nl",
            url: siteUrl("/"),
            foundingDate: "2016",
            description:
              "Wasz.nl is in 2016 opgericht door professionals met meer dan 25 jaar ervaring in vrachtwagenreiniging. Vanuit Almere bedienen wij heel transport-Nederland met mobiele truckwash en het digitale klantportaal WaszBase.",
            sameAs: ["https://www.facebook.com/Wasz.nl"],
          },
        },
      },
    ],
  });

  // Subtle photo card with caption — reuses existing styling tokens
  const PhotoCard = ({ src, alt, caption }) => (
    <figure
      className="my-12 rounded-2xl overflow-hidden border border-white/10 bg-[#0F0F23]"
      data-testid="story-photo"
    >
      <div className="aspect-[16/10] w-full bg-black/40">
        <img
          src={src}
          alt={alt}
          loading="lazy"
          decoding="async"
          className="w-full h-full object-cover"
          onError={(e) => {
            // Gracefully hide the image if the customer hasn't uploaded it yet
            e.currentTarget.parentElement.style.display = "none";
            const cap = e.currentTarget.closest("figure").querySelector("figcaption");
            if (cap) cap.style.display = "none";
          }}
        />
      </div>
      {caption && (
        <figcaption className="px-6 py-4 text-xs text-white/45 italic border-t border-white/5">
          {caption}
        </figcaption>
      )}
    </figure>
  );

  const Section = ({ id, eyebrow, title, children }) => (
    <section id={id} className="scroll-mt-28 mb-20">
      {eyebrow && (
        <p className="section-overline mb-3">{eyebrow}</p>
      )}
      <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-white leading-[1.15] mb-6">
        {title}
      </h2>
      <div className="text-white/70 text-base sm:text-[17px] leading-[1.75] space-y-5">
        {children}
      </div>
    </section>
  );

  // === NL content ===
  if (lang === "nl") {
    return (
      <main data-testid="story-page" className="min-h-screen pt-20 pb-10">
        {/* HERO */}
        <section className="relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a18] via-[#050509] to-[#050509] -z-10" />
          <div className="absolute inset-0 opacity-[0.06] -z-10 bg-[radial-gradient(circle_at_30%_20%,#00D4FF_0%,transparent_50%)]" />
          <div className="mx-auto max-w-4xl px-6 lg:px-8 pt-12 pb-20 sm:pt-16">
            <nav aria-label="Breadcrumb" className="mb-8 text-xs text-white/45 flex items-center gap-2">
              <Link to="/" className="hover:text-[#00D4FF]">Home</Link>
              <span>›</span>
              <span className="text-white/70">Het verhaal</span>
            </nav>
            <p className="section-overline mb-4 inline-flex items-center gap-2">
              <Heart className="w-3.5 h-3.5" /> Sinds 2016
            </p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter text-white leading-[1.02]">
              Het verhaal achter <span className="text-[#00D4FF]">Wasz.nl</span>.
            </h1>
            <p className="mt-7 text-white/65 text-lg sm:text-xl leading-relaxed max-w-3xl">
              Van een losse pomp en een goedkoop busje naar bijna tien jaar passie voor mobiele truckwash. Dit is hoe Wasz.nl écht is begonnen.
            </p>
          </div>
        </section>

        {/* BODY */}
        <article className="mx-auto max-w-3xl px-6 lg:px-8">

          <Section id="oorsprong" eyebrow="Hoe het begon" title="Werken bij de grondlegger van mobiele truckwash.">
            <p>
              Voordat Wasz.nl bestond, werkten wij bij het bedrijf dat in Nederland zo'n beetje de uitvinder is van mobiele truckwash. Dat was leerzaam, intensief en — eerlijk gezegd — geweldig. We leerden het vak vanaf de basis: hoe je een vrachtwagen netjes terugbrengt naar fabrieksglans, welke middelen écht werken op zwaar wegvuil, en welke routines een team scherp houden.
            </p>
            <p>
              Dat bedrijf groeide. Snel. Te snel, op een gegeven moment. Naarmate er meer klanten kwamen, schoof de aandacht steeds verder weg van de wasbeurt zelf. Wat ooit ambacht was, werd planning. En planning werd doorstroom.
            </p>
          </Section>

          <PhotoCard
            src={STORY_PHOTOS.begin}
            alt="Wasz.nl in de beginjaren — leren in het vak van mobiele truckwash"
            caption="De beginjaren. Lange dagen, vroege starts, en het besef dat we het anders wilden doen."
          />

          <Section id="verschil" eyebrow="Het keerpunt" title="Toen verschil zien moeilijker werd dan verschil maken.">
            <p>
              Op een bepaald moment zag je het in de auto's. Niet altijd, maar vaak genoeg. Velgen die nog vies waren. Spiegels die geen extra haal hadden gekregen. Chauffeurs begonnen te klagen. Niet extreem, maar het werd een patroon. Voor ons als wasprofessionals voelde dat verkeerd.
            </p>
            <p>
              We hadden er nooit bewust voor gekozen om snelheid boven kwaliteit te zetten. Maar in een vloot die groeit, sluipt dat erin. En als je zelf elke dag met het werk bezig bent, herken je het: een vrachtwagen die er goed bij staat, voelt anders dan een vrachtwagen die er goed genoeg bij staat.
            </p>
            <p>
              We wilden terug naar dat eerste gevoel. Liever één vrachtwagen perfect reinigen dan drie half afleveren.
            </p>
          </Section>

          <Section id="stap" eyebrow="De spannende stap" title="Een losse pomp, een goedkoop busje, en heel veel goede wil.">
            <p>
              Beginnen voor jezelf is spannend. Vooral als je een baan opzegt waar je goed in bent, met collega's waar je het mee kunt vinden. Maar we wisten: dit zit te diep. We wilden een bedrijf bouwen waar we zelf trots op konden zijn.
            </p>
            <p>
              Het startkapitaal was bescheiden. Geen wasstraat, geen vloot, geen mooie kantoorpand. Een losse hogedrukpomp, een tweedehands busje dat meer met goede wil reed dan met paardenkrachten, en een doos vol middelen die we zelf hadden uitgezocht omdat we wisten welke écht werkten.
            </p>
            <p>
              De eerste weken sliepen we niet veel. Niet uit stress — uit voorpret. Elke ochtend dat busje instappen voelde als terug naar waar het allemaal om moest draaien: het vak.
            </p>
          </Section>

          <PhotoCard
            src={STORY_PHOTOS.bus}
            alt="De servicebus van Wasz.nl met geopende zijdeur en uitrusting binnen handbereik"
            caption="Onze servicebus. Alle middelen, hogedruk en filters binnen handbereik — dezelfde gedachte als bij het eerste busje, maar nu volledig uitgerust."
          />

          <Section id="eerste-klant" eyebrow="Eerste rit" title="De eerste klant.">
            <p>
              Onze eerste klant hadden we te danken aan een telefoontje. Iemand die ons kende, wist wat we deden, en zei: kom maar. Geen contract, geen prijslijst, geen offerte. Gewoon vertrouwen.
            </p>
            <p>
              Die eerste werkdag duurde lang. We deden alles met de aandacht die je geeft aan iets waar je trots op wilt zijn. De velgen kregen tijd, de cabine kreeg tijd, de spiegels kregen tijd. Toen we klaar waren, stonden we naast elkaar en hebben we gelachen — opgelucht, een beetje onzeker nog, maar vooral trots.
            </p>
            <p>
              Het voelde voor het eerst alsof kwaliteit weer belangrijk mocht zijn.
            </p>
          </Section>

          <PhotoCard
            src={STORY_PHOTOS.pomp}
            alt="De uitrusting in de servicebus van Wasz.nl — slangen, borstels en pomp"
            caption="De uitrusting. Geen showroom, wel het hart van het werk."
          />

          <Section id="verschil-merken" eyebrow="Wat klanten merkten" title="Waarom klanten het verschil voelden.">
            <p>
              Mobiele truckwash is niet schaars in Nederland. Er zijn collega's die het goed doen en collega's die het volume najagen. Wij hebben nooit geprobeerd de goedkoopste te zijn, of de snelste. Wat we wel deden: er zijn — letterlijk — bij elke wasbeurt.
            </p>
            <p>
              Klanten merkten dat al snel. Niet aan grote dingen, maar aan kleine. Een chauffeur die zei dat de cabine "schoner schoon" was. Een planner die het verschil zag bij de eerste rit van de week. Iemand die belde om te zeggen dat hun klanten compliment hadden gegeven over de uitstraling van de vloot.
            </p>
            <p>
              Dat is wat passie voor het vak met zich meebrengt. Passie ziet je niet werken, maar je ziet het in het resultaat.
            </p>
          </Section>

          <Section id="kwaliteit" eyebrow="De keuze die we elke dag maken" title="Kwaliteit boven snelheid. Altijd.">
            <p>
              Het klinkt simpel, maar in onze sector is het een keuze die je elke dag opnieuw moet bevestigen. Een drukke planning verleidt tot tempo. Een lastige klantenwensen verleiden tot compromissen. Een chauffeur die haast heeft, verleidt tot overslaan.
            </p>
            <p>
              We hebben besloten: nee. We doen het zoals we het hadden willen leveren toen we zelf begonnen — met aandacht, met tijd, met respect voor het voertuig en voor de mensen die ermee werken.
            </p>
            <p>
              Dat heeft ons soms een wasbeurt gekost. Maar veel vaker heeft het ons een klant voor jaren opgeleverd.
            </p>
          </Section>

          <PhotoCard
            src={STORY_PHOTOS.werk}
            alt="Wasz.nl bij een vrachtwagen op locatie — werk in het vak"
            caption="Een gewone werkdag. Eén vrachtwagen tegelijk, helemaal af."
          />

          <Section id="vandaag" eyebrow="Bijna tien jaar later" title="Dezelfde visie. Een groter team.">
            <p>
              We bestaan nu bijna tien jaar. Het busje is vervangen, de pomp is opgeschaald, en wat begon als één vrachtwagen tegelijk groeide uit tot een operatie die in alle vijf de kernregio's van Nederland mobiele truckwash levert.
            </p>
            <p>
              Wat niet is veranderd: de manier waarop we kijken naar een vrachtwagen. Eerst zien, dan reinigen, daarna controleren. Met aandacht. Eén voertuig tegelijk.
            </p>
            <p>
              Wat wél is veranderd: de techniek eromheen. We bouwden <Link to="/waszbase" className="text-[#00D4FF] hover:underline">WaszBase</Link>, ons eigen klantportaal, omdat we wilden dat onze klanten niet alleen schone trucks krijgen, maar ook elk certificaat, elke factuur en elke historie per kenteken binnen handbereik hebben. Want kwaliteit zit niet alleen in de wasbeurt — kwaliteit zit ook in alles wat erna komt.
            </p>
          </Section>

          <Section id="vooruit" eyebrow="Waar we voor staan" title="Een bedrijf waar we trots op blijven.">
            <p>
              We bouwen Wasz.nl al bijna een decennium met dezelfde simpele overtuiging: passie voor het vak hoort centraal te staan. Niet als slogan, maar in de manier waarop we plannen, hoe we wassen, en hoe we onze klanten benaderen.
            </p>
            <p>
              Iedereen die voor Wasz.nl rijdt, wast of plant, weet hoe we het willen hebben. Niet omdat het in een handboek staat, maar omdat het in de manier zit waarop dit bedrijf is begonnen: met de keuze om het anders te doen, en die keuze elke dag opnieuw te maken.
            </p>
          </Section>

          {/* QUOTE */}
          <blockquote
            data-testid="story-pull-quote"
            className="my-16 rounded-2xl border border-white/10 bg-[#0F0F23] p-8 sm:p-10 relative"
          >
            <Quote className="absolute top-6 left-6 w-7 h-7 text-[#00D4FF]/30" strokeWidth={1.4} />
            <p className="pl-10 text-lg sm:text-xl text-white/85 italic leading-relaxed">
              Liever één vrachtwagen perfect reinigen dan drie half afleveren. Dat was tien jaar geleden onze keuze. Dat is hem vandaag nog steeds.
            </p>
          </blockquote>

          {/* CTAs */}
          <section className="not-prose mt-4 mb-10">
            <h2 className="text-xl sm:text-2xl font-bold text-white tracking-tight mb-2">
              Wilt u ervaren wat we bedoelen?
            </h2>
            <p className="text-white/55 text-sm sm:text-base mb-6 leading-relaxed">
              Of u nu een losse wasbeurt overweegt of een structureel reinigingscontract voor uw wagenpark — we kijken graag met u mee.
            </p>
            <div className="grid sm:grid-cols-3 gap-3">
              <Link
                to="/contact?type=quote"
                data-testid="story-cta-contact"
                className="group rounded-xl border border-white/10 bg-[#0F0F23] hover:border-[#00D4FF]/40 p-5 transition-colors"
              >
                <div className="flex items-center gap-2 mb-1.5">
                  <Sparkles className="w-4 h-4 text-[#00D4FF]" strokeWidth={1.8} />
                  <span className="text-sm font-semibold text-white">Neem contact op</span>
                </div>
                <p className="text-xs text-white/55 leading-relaxed">Vraag een offerte aan of stel uw vraag — we reageren binnen één werkdag.</p>
                <span className="inline-flex items-center gap-1 text-[11px] uppercase tracking-wider text-white/40 group-hover:text-[#00D4FF] transition-colors mt-3 font-semibold">
                  Naar contact <ArrowRight className="w-3 h-3" />
                </span>
              </Link>
              <Link
                to="/mobiele-truckwash"
                data-testid="story-cta-mobiele"
                className="group rounded-xl border border-white/10 bg-[#0F0F23] hover:border-[#00D4FF]/40 p-5 transition-colors"
              >
                <div className="flex items-center gap-2 mb-1.5">
                  <Truck className="w-4 h-4 text-[#00D4FF]" strokeWidth={1.8} />
                  <span className="text-sm font-semibold text-white">Bekijk mobiele truckwash</span>
                </div>
                <p className="text-xs text-white/55 leading-relaxed">Hoe we vandaag werken — op locatie, in heel Nederland, met dezelfde mentaliteit.</p>
                <span className="inline-flex items-center gap-1 text-[11px] uppercase tracking-wider text-white/40 group-hover:text-[#00D4FF] transition-colors mt-3 font-semibold">
                  Naar dienst <ArrowRight className="w-3 h-3" />
                </span>
              </Link>
              <Link
                to="/waszbase"
                data-testid="story-cta-waszbase"
                className="group rounded-xl border border-white/10 bg-[#0F0F23] hover:border-[#00D4FF]/40 p-5 transition-colors"
              >
                <div className="flex items-center gap-2 mb-1.5">
                  <Wrench className="w-4 h-4 text-[#00D4FF]" strokeWidth={1.8} />
                  <span className="text-sm font-semibold text-white">Meer over WaszBase</span>
                </div>
                <p className="text-xs text-white/55 leading-relaxed">Het klantportaal waarmee elke wasbeurt zichtbaar wordt — per kenteken.</p>
                <span className="inline-flex items-center gap-1 text-[11px] uppercase tracking-wider text-white/40 group-hover:text-[#00D4FF] transition-colors mt-3 font-semibold">
                  Naar WaszBase <ArrowRight className="w-3 h-3" />
                </span>
              </Link>
            </div>
          </section>
        </article>

        <CTASection />
      </main>
    );
  }

  // === EN content (shorter, same structure) ===
  return (
    <main data-testid="story-page" className="min-h-screen pt-20 pb-10">
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a18] via-[#050509] to-[#050509] -z-10" />
        <div className="mx-auto max-w-4xl px-6 lg:px-8 pt-12 pb-20 sm:pt-16">
          <nav aria-label="Breadcrumb" className="mb-8 text-xs text-white/45 flex items-center gap-2">
            <Link to="/" className="hover:text-[#00D4FF]">Home</Link>
            <span>›</span>
            <span className="text-white/70">Our story</span>
          </nav>
          <p className="section-overline mb-4 inline-flex items-center gap-2">
            <Heart className="w-3.5 h-3.5" /> Since 2016
          </p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter text-white leading-[1.02]">
            The story behind <span className="text-[#00D4FF]">Wasz.nl</span>.
          </h1>
          <p className="mt-7 text-white/65 text-lg sm:text-xl leading-relaxed max-w-3xl">
            From a single pump and a cheap van to nearly ten years of mobile truckwash. This is how Wasz.nl really started.
          </p>
        </div>
      </section>

      <article className="mx-auto max-w-3xl px-6 lg:px-8">
        <Section id="oorsprong" eyebrow="How it began" title="Working at the originator of mobile truckwash.">
          <p>Before Wasz.nl existed, we worked at the company that arguably invented mobile truckwash in the Netherlands. It was demanding, intensive, and frankly — wonderful. We learned the trade from the ground up.</p>
          <p>That company grew. Fast. Too fast at some point. As volume rose, attention shifted away from the wash itself. Craft became scheduling. Scheduling became throughput.</p>
        </Section>

        <PhotoCard src={STORY_PHOTOS.begin} alt="Wasz.nl in the early years" caption="The early years. Long days, early starts, and a growing sense that we wanted to do it differently." />

        <Section id="verschil" eyebrow="The turning point" title="When seeing the difference became harder than making one.">
          <p>You started to spot it in the vehicles. Rims still grimy. Mirrors that hadn't been touched again. A driver mentioning his last wash had taken longer. For wash professionals, that felt wrong.</p>
          <p>We had never consciously chosen speed over quality. But in a fleet that grows, it creeps in. Rather one truck cleaned perfectly than three delivered half-done.</p>
        </Section>

        <Section id="stap" eyebrow="The leap" title="One pump, one cheap van, a lot of intent.">
          <p>Starting on your own is scary. Especially when you leave a job you're good at, with colleagues you like. But we knew: this sits too deep. We wanted to build a company we could be proud of ourselves.</p>
          <p>Starting capital was modest. No wash bay, no fleet, no glossy office. One high-pressure pump. A second-hand van that ran more on intent than horsepower. A box of agents we'd picked ourselves because we knew which ones actually worked.</p>
        </Section>

        <PhotoCard src={STORY_PHOTOS.bus} alt="The first Wasz.nl van" caption="The first van. No frills, no stickers — but a promise." />

        <Section id="eerste-klant" eyebrow="First ride" title="The first customer.">
          <p>Our first customer came through a phone call. Someone who knew us, knew what we did, and said: just come. No contract, no quote. Trust.</p>
          <p>That first workday was long. We did everything with the attention you give something you want to be proud of. When we finished, we stood next to each other and laughed — relieved, still a little uncertain, but mostly proud.</p>
          <p>It felt, for the first time, like quality was allowed to matter again.</p>
        </Section>

        <PhotoCard src={STORY_PHOTOS.pomp} alt="The single pump from the early days" caption="The pump. Not heavy, not expensive — but the heart of the work." />

        <Section id="verschil-merken" eyebrow="What customers felt" title="Why customers noticed the difference.">
          <p>We never tried to be the cheapest or the fastest. What we did do: be there — literally — at every wash. Customers noticed quickly. Not in big ways, but in small ones. A driver saying the cabin was "cleaner-clean". A planner spotting the difference on the week's first trip.</p>
        </Section>

        <Section id="kwaliteit" eyebrow="The choice we make every day" title="Quality over speed. Always.">
          <p>It sounds simple, but in our trade it's a choice you reaffirm daily. A busy planning tempts you toward pace. We decided: no. We do it the way we'd want it delivered — one vehicle at a time, fully finished.</p>
        </Section>

        <PhotoCard src={STORY_PHOTOS.werk} alt="Wasz.nl at work" caption="An ordinary working day. One truck at a time, fully finished." />

        <Section id="vandaag" eyebrow="Almost ten years later" title="Same vision. Bigger team.">
          <p>The van's been replaced, the pump has scaled, and what began as one truck at a time grew into an operation serving all five of the Netherlands' core regions. What hasn't changed: how we look at a vehicle. See first, clean, then check. One vehicle at a time. What has changed: the technology around it. We built <Link to="/waszbase" className="text-[#00D4FF] hover:underline">WaszBase</Link>, our own customer portal, because quality isn't just in the wash — it's in everything that comes after.</p>
        </Section>

        <blockquote className="my-16 rounded-2xl border border-white/10 bg-[#0F0F23] p-8 sm:p-10 relative">
          <Quote className="absolute top-6 left-6 w-7 h-7 text-[#00D4FF]/30" strokeWidth={1.4} />
          <p className="pl-10 text-lg sm:text-xl text-white/85 italic leading-relaxed">
            Rather one truck cleaned perfectly than three delivered half-done. That was our choice ten years ago. It still is today.
          </p>
        </blockquote>

        <section className="not-prose mt-4 mb-10">
          <h2 className="text-xl sm:text-2xl font-bold text-white tracking-tight mb-2">
            Want to experience what we mean?
          </h2>
          <p className="text-white/55 text-sm sm:text-base mb-6 leading-relaxed">
            Whether you're considering a single wash or a structural cleaning contract — we'd love to take a look with you.
          </p>
          <div className="grid sm:grid-cols-3 gap-3">
            <Link to="/contact?type=quote" className="group rounded-xl border border-white/10 bg-[#0F0F23] hover:border-[#00D4FF]/40 p-5 transition-colors">
              <div className="flex items-center gap-2 mb-1.5"><Sparkles className="w-4 h-4 text-[#00D4FF]" /><span className="text-sm font-semibold text-white">Get in touch</span></div>
              <p className="text-xs text-white/55 leading-relaxed">Request a quote — we respond within one business day.</p>
            </Link>
            <Link to="/mobiele-truckwash" className="group rounded-xl border border-white/10 bg-[#0F0F23] hover:border-[#00D4FF]/40 p-5 transition-colors">
              <div className="flex items-center gap-2 mb-1.5"><Truck className="w-4 h-4 text-[#00D4FF]" /><span className="text-sm font-semibold text-white">See mobile truckwash</span></div>
              <p className="text-xs text-white/55 leading-relaxed">How we work today — on location, across the Netherlands.</p>
            </Link>
            <Link to="/waszbase" className="group rounded-xl border border-white/10 bg-[#0F0F23] hover:border-[#00D4FF]/40 p-5 transition-colors">
              <div className="flex items-center gap-2 mb-1.5"><Wrench className="w-4 h-4 text-[#00D4FF]" /><span className="text-sm font-semibold text-white">More about WaszBase</span></div>
              <p className="text-xs text-white/55 leading-relaxed">The portal that makes every wash visible — per license plate.</p>
            </Link>
          </div>
        </section>
      </article>

      <CTASection />
    </main>
  );
};

export default Story;
